// src/Dashboard/pages/user/Galleries/components/RemovePhotoDrawer.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaSearch, FaTimes, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";

import {
  deletePhoto,
  getPhotosByGalleryId,
} from "../../../../../services/photoServices";

import { toAbsolutePhotoUrl as toAbsPhoto } from "../../../../../services/albumPhotoServices";

import {
  FALLBACK_IMAGE,
  onImgError,
  stripHtml,
} from "../utils/galleriesUtils";

export default function RemovePhotoDrawer({
  isOpen,
  galleryId,
  onClose,
  onDeleted,
}) {
  const selectRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const [photos, setPhotos] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const timer = window.setTimeout(() => {
      selectRef.current?.focus();
    }, 80);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !galleryId) return;

    const fetchPhotos = async () => {
      try {
        setLoading(true);
        setConfirm(false);
        setSearch("");

        const data = await getPhotosByGalleryId(galleryId);
        const safePhotos = Array.isArray(data) ? data : [];

        setPhotos(safePhotos);
        setSelectedId(safePhotos.length > 0 ? String(safePhotos[0].id) : "");
      } catch (err) {
        console.error("Erreur chargement photos :", err);
        setPhotos([]);
        setSelectedId("");
        toast.error("Impossible de charger les photos.");
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, [isOpen, galleryId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return photos;

    return photos.filter((photo) =>
      stripHtml(photo?.description || "").toLowerCase().includes(q)
    );
  }, [photos, search]);

  const selected = useMemo(() => {
    return (
      filtered.find((photo) => String(photo.id) === String(selectedId)) || null
    );
  }, [filtered, selectedId]);

  const previewUrl = toAbsPhoto(selected?.photo_url || "");
  const previewCaption = stripHtml(selected?.description || "");
  const canDelete = !!selected && confirm && !deleting;

  if (!isOpen) return null;

  const handleDelete = async (event) => {
    event.preventDefault();

    if (!selected) {
      toast.warn("Choisissez une photo à supprimer.");
      return;
    }

    if (!confirm) {
      toast.warn("Cochez la confirmation pour supprimer.");
      return;
    }

    try {
      setDeleting(true);

      await deletePhoto(Number(selected.id));

      toast.success("Photo supprimée.");
      await onDeleted?.();
    } catch (err) {
      console.error("Erreur suppression photo :", err);
      toast.error("Erreur lors de la suppression de la photo.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-red-600 via-pink-600 to-orange-500" />

        <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-600 dark:text-red-300">
              Suppression
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              Retirer une photo de l’album
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaTimes />
          </button>
        </header>

        <form
          onSubmit={handleDelete}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-[1fr_240px]">
                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Rechercher par légende
                  </label>

                  <div className="relative">
                    <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                    <input
                      type="text"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Quelques mots..."
                      className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-9 pr-4 text-slate-900 outline-none transition focus:ring-2 focus:ring-red-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Photo
                  </label>

                  <select
                    ref={selectRef}
                    value={selectedId}
                    onChange={(event) => {
                      setSelectedId(event.target.value);
                      setConfirm(false);
                    }}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-red-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  >
                    {filtered.length === 0 ? (
                      <option value="">Aucune photo</option>
                    ) : (
                      filtered.map((photo, index) => {
                        const label =
                          stripHtml(photo?.description || "") ||
                          `Photo #${photo.id || index + 1}`;

                        return (
                          <option key={photo.id} value={photo.id}>
                            {label.length > 80
                              ? `${label.slice(0, 80)}…`
                              : label}
                          </option>
                        );
                      })
                    )}
                  </select>
                </div>
              </div>

              {loading && (
                <div className="h-64 animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
              )}

              {!loading && filtered.length === 0 && (
                <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-5 py-12 text-center text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-300">
                  Aucune photo trouvée.
                </div>
              )}

              {!loading && selected && (
                <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
                  <div className="w-full" style={{ aspectRatio: "16 / 9" }}>
                    <img
                      src={previewUrl || FALLBACK_IMAGE}
                      alt={previewCaption || "Aperçu"}
                      className="h-full w-full object-contain"
                      onError={onImgError}
                    />
                  </div>

                  {previewCaption && (
                    <div className="border-t border-slate-200 px-4 py-3 text-sm text-slate-600 dark:border-slate-800 dark:text-slate-300">
                      {previewCaption}
                    </div>
                  )}
                </div>
              )}

              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                <input
                  type="checkbox"
                  checked={confirm}
                  onChange={(event) => setConfirm(event.target.checked)}
                  className="accent-red-600"
                />
                Je confirme supprimer définitivement cette photo.
              </label>
            </div>
          </div>

          <footer className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95">
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={deleting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canDelete}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaTrash />
                {deleting ? "Suppression..." : "Supprimer"}
              </button>
            </div>
          </footer>
        </form>
      </aside>
    </div>
  );
}